'use client'

import { cva, VariantProps } from 'class-variance-authority'
import { isNil } from 'lodash-es'
import { ComponentProps, ElementType, ReactNode, RefObject } from 'react'

import { cn, isDeleteKeyboardEvent, useFixWidth } from '@/shared/lib'

const OFFSET = 12

const chipVariants = cva(
  [
    'flex cursor-pointer items-center justify-center font-medium transition-colors',
    'ring-offset-background focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
    '[&_div]:w-fit',
    '[&_svg]:size-4 [&_svg]:text-black',
  ],
  {
    variants: {
      variant: {
        solid: [
          'bg-gray-50 text-gray-500',
          'hover:border-transparent hover:bg-purple-50 hover:text-black',
          'active:bg-black active:text-white active:[&_svg]:text-white',
        ],
        outline: [
          'border border-gray-200 bg-white text-gray-400',
          'hover:bg-purple-50 hover:text-black',
          'active:border-black active:bg-purple-50',
        ],
      },
      size: {
        medium: 'typo-label2 h-8 px-2.5 py-1.5',
        large: 'typo-label1 h-9.5 px-2.5 py-2',
      },
      shape: {
        pill: 'rounded-full',
        rounded: 'rounded-sm',
      },
      selected: {
        true: '',
        false: '',
      },
    },

    compoundVariants: [
      {
        variant: 'solid',
        selected: true,
        className: [
          'bg-black text-white [&_svg]:text-white',
          'hover:bg-black hover:text-white hover:[&_svg]:text-white',
        ],
      },
      {
        variant: 'outline',
        selected: true,
        className: 'border-black text-black',
      },
    ],
  },
)

type ChipProps = VariantProps<typeof chipVariants> & {
  selected?: boolean
  startIcon?: ReactNode
  endIcon?: ReactNode
  component?: ElementType
  children: ReactNode
  childrenRef?: RefObject<HTMLDivElement | null>
}

type Props<E extends ElementType> = ChipProps &
  Omit<ComponentProps<E>, keyof ChipProps>

export const Chip = <E extends ElementType>({
  variant = 'outline',
  size = 'medium',
  shape = 'pill',
  selected = false,
  component,
  startIcon,
  endIcon,
  ref,
  onClick,
  onKeyDown,
  onKeyUp,
  onDelete,
  width: widthProp,
  className,
  children,
  childrenRef: childrenRefProp,
  ...props
}: Props<E>) => {
  const { width: widthFromChildren, childrenRef } = useFixWidth<HTMLDivElement>(
    children,
    { offset: OFFSET },
  )

  const $Element = component || 'button'

  const width = isNil(widthProp) ? widthFromChildren : widthProp

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.currentTarget === event.target && isDeleteKeyboardEvent(event)) {
      // Will be handled in keyUp, otherwise some browsers
      // might init navigation
      event.preventDefault()
    }

    if (onKeyDown) {
      onKeyDown(event)
    }
  }

  const handleKeyUp = (event: KeyboardEvent) => {
    if (event.currentTarget === event.target) {
      if (onDelete && isDeleteKeyboardEvent(event)) {
        onDelete(event)
      }
    }

    if (onKeyUp) {
      onKeyUp(event)
    }
  }

  return (
    <$Element
      ref={ref}
      className={cn(
        chipVariants({ variant, size, shape, selected, className }),
      )}
      style={width && { width: `${width}px` }}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      data-selected={selected}
      {...props}
    >
      <div
        className="flex items-center justify-center gap-1 whitespace-nowrap"
        ref={childrenRefProp || childrenRef}
      >
        {startIcon}
        <div>{children}</div>
        {endIcon}
      </div>
    </$Element>
  )
}
